import { useState, useRef, useEffect, useContext } from 'react';
import { MessageCircle, X, Send, MinusCircle, User, Bot, Sparkles } from 'lucide-react';
import axios from 'axios';
import { API, AuthContext } from '../App';

const QUICK_OPTIONS = [
  { id: 'complaint', label: '😤 Complaint', color: 'bg-red-500/20 text-red-400' },
  { id: 'suggestion', label: '💡 Suggestion', color: 'bg-yellow-500/20 text-yellow-400' },
  { id: 'compliment', label: '💜 Compliment', color: 'bg-pink-500/20 text-pink-400' },
  { id: 'question', label: '❓ Question', color: 'bg-blue-500/20 text-blue-400' },
];

export default function SupportChatWidget() {
  const { user } = useContext(AuthContext);
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Initial bot message
      setMessages([{
        id: '1',
        type: 'bot',
        text: `Hi${user?.name ? ` ${user.name.split(' ')[0]}` : ''}! 👋 Welcome to Darasani Support.\n\nHow can I help you today?`,
        timestamp: new Date()
      }]);
    }
  }, [isOpen, user]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleQuickOption = (option) => {
    setSelectedType(option.id);
    addMessage('user', option.label);
    
    const responses = {
      complaint: "I'm sorry you're experiencing issues. Please describe your complaint and I'll make sure it gets addressed.",
      suggestion: "We love feedback! 💡 What suggestion would you like to share?",
      compliment: "That's wonderful! 😊 What made your experience special?",
      question: "I'll do my best to help! What's your question?"
    };
    
    setTimeout(() => addMessage('bot', responses[option.id]), 500);
  };

  const addMessage = (type, text) => {
    setMessages(prev => [...prev, {
      id: Date.now().toString(),
      type,
      text,
      timestamp: new Date()
    }]);
  };

  const sendMessage = async () => {
    if (!inputText.trim() || loading) return;

    const userMessage = inputText.trim();
    setInputText('');
    addMessage('user', userMessage);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/support/chat`, {
        message: userMessage,
        message_type: selectedType || 'question',
        user_id: user?.id,
        user_name: user?.name,
        user_email: user?.email
      });

      setTimeout(() => {
        addMessage('bot', res.data.response);
        if (res.data.needs_human_agent) {
          setTimeout(() => {
            addMessage('bot', '📞 I\'ve notified our support team. A human agent will reach out soon.');
          }, 1000);
        }
      }, 500);
    } catch (error) {
      addMessage('bot', 'Sorry, I encountered an error. Please try again or email us at support@darasani.com');
    }

    setLoading(false);
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-24 right-6 w-14 h-14 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full shadow-lg shadow-indigo-500/25 flex items-center justify-center text-white hover:scale-110 transition-transform z-50"
        data-testid="support-chat-button"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    );
  }

  return (
    <div 
      className={`fixed bottom-24 right-6 z-50 transition-all duration-300 ${
        isMinimized ? 'w-72' : 'w-96'
      }`}
      data-testid="support-chat-widget"
    >
      <div className="bg-slate-900 rounded-2xl shadow-2xl border border-slate-700 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm">Darasani Support</h3>
              <p className="text-indigo-200 text-xs">AI-powered assistance</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-1.5 hover:bg-white/10 rounded-lg text-white/80"
            >
              <MinusCircle className="w-4 h-4" />
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1.5 hover:bg-white/10 rounded-lg text-white/80"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {!isMinimized && (
          <>
            {/* Messages */}
            <div className="h-80 overflow-y-auto p-4 space-y-3">
              {/* Quick Options */}
              {!selectedType && messages.length <= 1 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {QUICK_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => handleQuickOption(option)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium ${option.color} hover:opacity-80 transition-opacity`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}

              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.type === 'bot' && (
                    <div className="w-6 h-6 bg-indigo-500/20 rounded-full flex items-center justify-center mr-2 flex-shrink-0">
                      <Bot className="w-3 h-3 text-indigo-400" />
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] px-3 py-2 rounded-2xl ${
                      msg.type === 'user'
                        ? 'bg-indigo-600 text-white rounded-br-md'
                        : 'bg-slate-800 text-slate-200 rounded-bl-md'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                    <p className={`text-[10px] mt-1 ${msg.type === 'user' ? 'text-indigo-200' : 'text-slate-500'}`}>
                      {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  {msg.type === 'user' && (
                    <div className="w-6 h-6 bg-slate-700 rounded-full flex items-center justify-center ml-2 flex-shrink-0">
                      <User className="w-3 h-3 text-slate-400" />
                    </div>
                  )}
                </div>
              ))}
              
              {loading && (
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-indigo-500/20 rounded-full flex items-center justify-center">
                    <Bot className="w-3 h-3 text-indigo-400" />
                  </div>
                  <div className="bg-slate-800 px-4 py-2 rounded-2xl rounded-bl-md">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-3 border-t border-slate-700">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder="Type your message..."
                  className="flex-1 bg-slate-800 border border-slate-700 rounded-xl px-4 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500"
                  data-testid="support-chat-input"
                />
                <button
                  onClick={sendMessage}
                  disabled={!inputText.trim() || loading}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl text-white"
                  data-testid="support-chat-send"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
